import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

export const kungalvPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-kungalv",
    serviceCategory: "bortforsling",
    city: "göteborg",

    meta: {
      title: "Bortforsling Kungälv – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Kungälv. Vi hämtar möbler, vitvaror och skräp i Kungälv, Ytterby, Kareby, Kärna, Kode och Marstrand. Fast pris, ofta samma dag.",
      h1: "Bortforsling Kungälv",
    },

    hero: {
      heading: "Bortforsling i Kungälv",
      intro:
        "Boka bortforsling i Kungälv – vi hämtar möbler, vitvaror och skrymmande föremål i hela kommunen, från lägenheterna i Komarken och Kongahälla till villorna i Ytterby, Kareby och Kärna och husen ute på Marstrand. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Tusentals slutförda uppdrag i Göteborgsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Kungälv – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Kungälv",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Kungälv och körs till Munkegärde återvinningscentral",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Ytterby, Kungälv",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i centrala Kungälv",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Komarken, Kungälv",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Kareby, Kungälv",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling på Marstrand, Kungälv",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Kungälv – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Kungälv är en kommun i södra Bohuslän strax norr om Göteborg, mellan Göta älv och Nordre älv, med Bohus fästning som landmärke vid älvmötet. Bebyggelsen är varierad: lägenhetsområdena Komarken och nya Kongahälla i centrala Kungälv, villakvarteren i Ytterby, Kareby och Kärna, byarna Kode och Diseröd, samt den bilfria ön Marstrand ute i havsbandet. Den blandningen gör bortforsling till olika utmaningar: i lägenheten handlar det ofta om trapphus och hiss, i villan om ett fullt garage eller förråd, och på Marstrand om att samordna transporten med färjan. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Kungälv har en stor fördel här – kommunen driver tre återvinningscentraler: Munkegärde i centrala Kungälv, samt Kärna och Kode för de västra och norra delarna. Det gör att det nästan alltid finns en station nära din adress. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E6 går rakt genom Kungälv och Bohusbanan stannar i Ytterby tar vi oss snabbt mellan orterna, och vi planerar Marstrandsuppdrag tillsammans med färjan från Koön. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både lägenhetskvarteren i Komarken och de smala gränderna i gamla Kungälv och på Marstrand – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Kungälv",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Ytterby, Kareby, Kärna eller Diseröd är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet i Komarken eller Kongahälla, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten. Bor du på Marstrand, berätta var på ön föremålen finns så planerar vi transporten med färjan.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Kungälvsbo på Munkegärde, Kärna eller Kode återvinningscentral.",
      additionalSections: [
        {
          heading: "Orter och områden vi täcker i Kungälv",
          content:
            "Vi utför bortforsling i hela Kungälvs kommun – från de centrala lägenhetsområdena till villakvarteren, byarna och skärgården. Oavsett om du bor i en lägenhet i Komarken, en villa i Ytterby, ett hus i Kärna eller en sommarbostad på Marstrand kan vi vara på plats, ofta redan samma dag.\n\nI centrala Kungälv – med Komarken, Kongahälla, Munkegärde och gamla stan kring Västra gatan – har vi stor vana vid både lägenhetshämtningar med hiss och trapphus och de smala gränderna i den historiska trähusbebyggelsen. I Ytterby och Kareby möter vi villa- och radhusbebyggelse nära Bohusbanan.\n\nVi är minst lika snabba i kommunens övriga delar: Kärna och Tjuvkil i väster, Kode och Diseröd i norr, samt Marstrand med Koön ute i havsbandet. På den bilfria ön Marstrand samordnar vi alltid transporten med färjan från Koön. Tack vare närheten till E6, Bohusbanan och Marstrandsvägen planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon ort.",
        },
        {
          heading: "Återvinning och miljöansvar i Kungälv",
          content:
            "En viktig del av vår bortforsling i Kungälv är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler. Kungälv har en stor fördel eftersom kommunen driver tre stationer på olika sidor av kommunen:\n\nMunkegärde återvinningscentral – huvudstationen i centrala Kungälv, där sorterat grovavfall, elavfall, förpackningar och farligt avfall tas emot, liksom föremål för återbruk. Kärna återvinningscentral – för uppdrag i de västra delarna mot kusten. Kode återvinningscentral – för uppdrag i de norra delarna. Vi väljer alltid den station som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Stadsmissionen och Emmaus. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Kungälv med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Kungälvs orter, från lägenhetskvarteren i Komarken och Kongahälla till villatomterna i Ytterby och husen på Marstrand, och vet att kommunens tre återvinningscentraler ligger spridda nära dig. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Göteborgsregionen och E6 går rakt genom Kungälv. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon ort i Kungälv, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Munkegärde, Kärna och Kode återvinningscentral. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Kungälv?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon ort i Kungälv.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Kungälv?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Kungälv?",
        answer:
          "Vi har skåpbilar och personal i hela Göteborgsregionen och E6 går rakt genom Kungälv, så vi tar oss snabbt mellan orterna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Kungälv?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Kungälv?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Komarken och Kongahälla. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa och garage i Kungälv?",
        answer:
          "Ja, en stor del av våra uppdrag i Kungälv är villahämtningar i Ytterby, Kareby, Kärna och Diseröd. Vi backar in på uppfarten där det går och tömmer garage, källare och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni göra bortforsling på Marstrand?",
        answer:
          "Ja, vi utför bortforsling på Marstrand trots att ön är bilfri. Vi samordnar transporten med färjan från Koön och bär ut, lastar och kör bort dina saker. Berätta vid bokningen var på ön föremålen finns så planerar vi upplägget.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Kungälv försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Kungälv?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. Kungälv driver tre stationer – Munkegärde, Kärna och Kode – så det finns alltid en nära din adress. Det som kan återanvändas skänks till Stadsmissionen eller Emmaus.",
      },
      {
        question: "Kan jag boka bortforsling i Kungälv på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Kungälv?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid både villaförråden i Ytterby och Kärna och lägenhetsförråden i Komarken.",
      },
      {
        question: "Vilka orter i Kungälv täcker ni?",
        answer:
          "Vi utför bortforsling i hela Kungälv – centrala Kungälv (Komarken, Kongahälla, Munkegärde), Ytterby, Kareby, Kärna, Tjuvkil, Kode, Diseröd samt Marstrand och Koön. Inga extra avgifter för någon ort.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Kungälv?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som Kungälvsbo på Munkegärde, Kärna eller Kode återvinningscentral.",
      },
      {
        question: "Vilka kommuner runt Kungälv utför ni bortforsling i?",
        answer: `Vi finns i hela Göteborgsregionen, inklusive följande kommuner: ${goteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Kungälv i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Kungälv kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Kungälv idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Kungälvs kommun.",
    },

    en: {
      slug: "junk-removal-kungalv",
      meta: {
        title: "Junk Removal Kungälv – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Kungälv. We pick up furniture, appliances and bulky items in Kungälv, Ytterby, Kareby, Kärna, Kode and Marstrand. Fixed price, often same day.",
        h1: "Junk Removal Kungälv",
      },
      hero: {
        heading: "Junk Removal in Kungälv",
        intro:
          "Book junk removal in Kungälv – we collect furniture, appliances and bulky items across the whole municipality, from the apartments in Komarken and Kongahälla to the villas in Ytterby, Kareby and Kärna and the houses out on Marstrand. Quick booking, fixed price and often available the same day. Thousands of completed jobs in the Gothenburg region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Kungälv – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Kungälv",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Kungälv and driven to Munkegärde recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Ytterby, Kungälv",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in central Kungälv",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Komarken, Kungälv",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Kareby, Kungälv",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal on Marstrand, Kungälv",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Kungälv – fast, hassle-free and responsible",
        mainText:
          "Kungälv is a municipality in southern Bohuslän just north of Gothenburg, between the Göta älv and Nordre älv rivers, with the Bohus Fortress as a landmark at the meeting of the rivers. The housing is varied: the apartment areas of Komarken and the new Kongahälla in central Kungälv, the villa neighbourhoods of Ytterby, Kareby and Kärna, the villages of Kode and Diseröd, and the car-free island of Marstrand out in the archipelago. That mix makes junk removal a different challenge: in an apartment it is often stairwells and elevators, in a villa a full garage or storage room, and on Marstrand coordinating the transport with the ferry. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. Kungälv has a big advantage here – the municipality runs three recycling centres: Munkegärde in central Kungälv, plus Kärna and Kode for the western and northern parts. This means there is almost always a station near your address. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E6 runs straight through Kungälv and the Bohusbanan railway stops at Ytterby, we move quickly between the towns, and we plan Marstrand jobs together with the ferry from Koön. This means we can often be on site the same day you book. Our workers are used to both the apartment quarters of Komarken and the narrow lanes of old Kungälv and Marstrand – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Kungälv",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Ytterby, Kareby, Kärna or Diseröd, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment in Komarken or Kongahälla, check whether there is a loading zone, courtyard or visitor parking near the entrance. If you live on Marstrand, tell us where on the island the items are so we can plan the ferry transport.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Kungälv resident you can drop those off for free at the Munkegärde, Kärna or Kode recycling centre.",
        additionalSections: [
          {
            heading: "Towns and areas we cover in Kungälv",
            content:
              "We perform junk removal across the entire municipality of Kungälv – from the central apartment areas to the villa neighbourhoods, the villages and the archipelago. Whether you live in an apartment in Komarken, a villa in Ytterby, a house in Kärna or a summer home on Marstrand, we can be on site, often the same day.\n\nIn central Kungälv – with Komarken, Kongahälla, Munkegärde and the old town around Västra gatan – we are highly experienced with both apartment pickups via elevator and stairwell and the narrow lanes of the historic wooden-house district. In Ytterby and Kareby we meet villa and townhouse housing near the Bohusbanan.\n\nWe are just as quick in the other parts of the municipality: Kärna and Tjuvkil in the west, Kode and Diseröd in the north, and Marstrand with Koön out in the archipelago. On the car-free island of Marstrand we always coordinate the transport with the ferry from Koön. Thanks to the proximity of the E6, the Bohusbanan and Marstrandsvägen, we always plan the route in advance so you don't have to wait. No extra charges for any town.",
          },
          {
            heading: "Recycling and environmental responsibility in Kungälv",
            content:
              "An important part of our junk removal in Kungälv is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres. Kungälv has a big advantage because the municipality runs three stations on different sides of the municipality:\n\nMunkegärde recycling centre – the main station in central Kungälv, which receives sorted bulky waste, e-waste, packaging and hazardous waste, as well as items for reuse. Kärna recycling centre – for jobs in the western parts towards the coast. Kode recycling centre – for jobs in the northern parts. We always choose the station nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Stadsmissionen and Emmaus. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Kungälv with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Kungälv's towns, from the apartment quarters of Komarken and Kongahälla to the villa plots of Ytterby and the houses on Marstrand, and that the municipality's three recycling centres are spread out close to you. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Gothenburg region and the E6 runs straight through Kungälv. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any town in Kungälv, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via the Munkegärde, Kärna and Kode recycling centres. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Kungälv cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any town in Kungälv.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Kungälv?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Kungälv?",
          answer:
            "We have vans and staff across the Gothenburg region and the E6 runs straight through Kungälv, so we move quickly between the towns. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Kungälv?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Kungälv?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Komarken and Kongahälla. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas and garages in Kungälv?",
          answer:
            "Yes, a large share of our jobs in Kungälv are villa pickups in Ytterby, Kareby, Kärna and Diseröd. We reverse onto the driveway where possible and clear garages, basements and storage rooms. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you do junk removal on Marstrand?",
          answer:
            "Yes, we perform junk removal on Marstrand even though the island is car-free. We coordinate the transport with the ferry from Koön and carry out, load and remove your items. Tell us at booking where on the island the items are so we can plan the setup.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Kungälv insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Kungälv?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Kungälv runs three stations – Munkegärde, Kärna and Kode – so there is always one near your address. Items that can be reused are donated to Stadsmissionen or Emmaus.",
        },
        {
          question: "Can I book junk removal in Kungälv on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Kungälv?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage rooms, attics and garages. We are used to both the villa storage rooms in Ytterby and Kärna and the apartment storage units in Komarken.",
        },
        {
          question: "Which towns in Kungälv do you cover?",
          answer:
            "We perform junk removal across all of Kungälv – central Kungälv (Komarken, Kongahälla, Munkegärde), Ytterby, Kareby, Kärna, Tjuvkil, Kode, Diseröd, and Marstrand and Koön. No extra charges for any town.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Kungälv?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Kungälv resident you can drop those off for free at the Munkegärde, Kärna or Kode recycling centre.",
        },
        {
          question: "Which municipalities around Kungälv do you serve?",
          answer:
            "We operate across the entire Gothenburg region, including Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Kungälv in 3 easy steps",
        step2Text:
          "Our young workers in Kungälv come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Kungälv today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole municipality of Kungälv.",
      },
    },
  },
];
